import React, { Fragment, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Table } from 'antd';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import H3Text from '@Uilib/h3Text';
import { QUANTITY } from '@Apis/constants';
import ProductFilterV2 from '@Components/Common/ProductFilterV2';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import BatchesTable from '@Components/Common/BatchesTable';
import ProductCategoryLabel from '../../../../Common/ProductCategoryLabel';
import PRZModal from '../../../../Common/UI/PRZModal';
import './style.scss';

const ConsumptionLinesView = ({
  data, loading, handleProductChangeValue, handleProductChange, formSubmitted, addNewRow, tenantDepartmentId, updateData, user, tenantId, selectedCustomer, cfConsumptionLine, visibleColumns, customLineInputChange, isSalesOrderPs, consumptionLines, cfV2DocConsumption,
}) => {

  const [showPRZModal, setShowPRZModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  const getColumns = () => {
    const productListColumns = [
      {
        title: 'Product',
        fixed: 'left',
        width: '250px',
        render: (text, record) => (
          <div style={{ minWidth: '250px', maxWidth: '250px' }}>
            {record?.product_sku_info
              ? (
                <div className="ps-table__product-name">
                  <div className="flex-display">
                    <div>
                      <div onClick={() => { setShowPRZModal(true); setSelectedDocumentId(record?.product_sku_info?.product_sku_id); }}>
                        <a>
                          {`#${record?.product_sku_info?.internal_sku_code}`}
                        </a>
                      </div>
                      <div>
                        {record?.product_sku_name}
                      </div>
                    </div>
                  </div>

                  {record?.product_category_info?.category_path?.length > 0 && (
                    <ProductCategoryLabel
                      categoryPath={record?.product_category_info?.category_path}
                      categoryName={record?.product_category_info?.category_path?.at(-1)}
                      containerStyle={{
                        width: 'fit-content',
                      }}
                    />
                  )}
                  {record?.remarks && (
                    <div
                      dangerouslySetInnerHTML={{ __html: record.remarks || '' }}
                      className="table-subscript"
                      style={{
                        width: '200px',
                        whiteSpace: 'pre-wrap',
                      }}
                    />
                  )}
                  <div style={{ display: 'flex' }}>
                    <div style={{ marginTop: '4px' }}>
                      {(record?.product_sku_info?.product_type === 'BUNDLE') && <H3Text text="Bundle" className="table-subscript table-subscript-tag" />}
                      {(record?.product_sku_info?.product_type === 'SERVICE') && <H3Text text="Service" className="table-subscript table-subscript-tag" />}
                    </div>
                    {record?.available_qty ? (
                      <div className="stock-value table-subscript table-subscript-tag" style={{ whiteSpace: 'nowrap' }}>
                        {`${record?.available_qty} ${record?.product_sku_info?.uom_info?.uqc?.toProperCase()}`}
                      </div>
                    ) : ''}
                  </div>
                </div>
              ) : (
                <React.Fragment>
                  <ProductFilterV2
                    record={record}
                    productTypes={['STORABLE', 'SERVICE', 'BUNDLE', 'NON_STORABLE']}
                    handleProductChangeValue={handleProductChangeValue}
                    handleProductChange={handleProductChange}
                    required
                    showError={formSubmitted && !record.product_sku_name}
                    isSalesProduct
                    onClear={(key) => {
                      const copyData = JSON.parse(JSON.stringify(data));
                      const newData = copyData.map((item) => {
                        if (item.key === key) {
                          return {
                            key: uuidv4(),
                            product_sku_name: '',
                            quantity: '',
                            batch_number: '',
                            product_batches: [],
                            packed_quantity: '',
                            product_sku_id: null,
                            product_sku_info: null,
                          };
                        }
                        return {
                          ...item,
                        };
                      });
                      addNewRow();
                      updateData(newData);
                    }}
                    excludedProducts={data.map((item) => JSON.stringify(item?.tenant_product_id)) || []}
                    disabled={!tenantDepartmentId}
                    tenantDepartmentId={tenantDepartmentId}
                    showClear
                    filterReservedQuantity
                  />

                </React.Fragment>
              )}
          </div>
        ),
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: 'Quantity',
        dataIndex: 'order_quantity',
        width: '100px',
        render: (text, record) => {
          const uqc = record?.uom_info?.[0]?.uqc?.toProperCase() || '';
          const precision = record?.uom_info?.[0]?.precision;
          return (
            <div style={{ width: '100px' }}>
              {`${QUANTITY(Math.abs(record?.quantity), precision)} ${uqc}`}
            </div>
          )
        },
        visible: visibleColumns?.QUANTITY?.visible,
      },
    ];

    if (cfConsumptionLine?.length) {
      productListColumns.splice(3, 0, ...CustomFieldHelpers.renderCustomLineColumns(false, cfConsumptionLine, visibleColumns, customLineInputChange, formSubmitted, '', '', '', '', '', '', '', false, cfV2DocConsumption));
    }

    return productListColumns.filter((item) => item.visible);
  };

  const bundleListColumns = [
    {
      title: 'SKU#',
      render: (record) => (
        <Fragment>
          <div onClick={() => { setShowPRZModal(true); setSelectedDocumentId(record?.product_sku_info?.product_sku_id); }}>
            <a>
              {`#${record?.product_sku_info?.internal_sku_code}`}
            </a>
          </div>
        </Fragment>
      ),
    },
    {
      title: 'PRODUCT',
      width: '300px',
      render: (text, record) => (
        <div className="ps-table__product-name">
          {record?.product_sku_info?.product_sku_name}
        </div>
      ),
    },
    {
      title: 'QUANTITY',
      dataIndex: 'quantity',
      width: '120px',
      render: (text, record) => (
        <div className="ps-qty-input">
          {`${record?.bundle_quantity} ${record?.uom_info?.[0]?.uqc?.toProperCase()}`}
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <div className="ps-lines__wrapper">
        <Table
          bordered
          showHeader
          title={() => (
            <div className="ps_lines-title__wrapper">
              <H3Text text={`Consumption Items (${consumptionLines?.length})`} className="create-ps__input-row__label" />
            </div>
          )}
          size="small"
          columns={getColumns()}
          dataSource={(consumptionLines || [])?.map((item, index) => ({
            ...item,
            key: item.order_line_id || index,
            product_category_info: item?.product_sku_info?.product_category_info,
          }))}
          pagination={false}
          scroll={{ x: true }}
          loading={loading}
          expandable={{
            rowExpandable: (record) => record?.product_batches?.length || record?.bundle_products?.length,
            expandedRowRender: (record) => (
              <React.Fragment>
                {record.bundle_products?.length ? (
                  <div className="view-bundle__inner-table">
                    <Table
                      bordered
                      showHeader
                      size="small"
                      scroll="scroll"
                      columns={bundleListColumns}
                      dataSource={record?.bundle_products?.map((item) => ({
                        ...item,
                        bundle_quantity: parseFloat(item?.quantity || '0'),
                        key: item.order_line_id,
                      }))}
                      pagination={false}
                      expandable={{
                        defaultExpandAllRows: false,
                        rowExpandable: (innerRecord) => innerRecord?.product_batches?.length || innerRecord?.bundle_products?.length,
                        expandedRowRender: (innerRecord) => (
                          <React.Fragment>
                            <BatchesTable
                              title="Stock"
                              batches={innerRecord?.product_batches}
                              uomInfo={innerRecord?.uom_info?.[0]}
                              expiryDays={innerRecord?.product_sku_info?.expiry_days}
                              productInfo={innerRecord?.product_sku_info}
                            />
                          </React.Fragment>
                        ),
                      }}
                    />
                  </div>
                )
                  : (
                    <React.Fragment>
                      <BatchesTable
                        title="Stock"
                        batches={record?.product_batches}
                        uomInfo={record?.uom_info?.[0]}
                        expiryDays={record?.product_sku_info?.expiry_days}
                        productInfo={record?.product_sku_info}
                      />
                    </React.Fragment>
                  )}
              </React.Fragment>
            ),
          }}
        />
      </div>
      <PRZModal
        isOpen={showPRZModal}
        onClose={() => {
          setShowPRZModal(false);
          setSelectedDocumentId(null);
        }}
        entityType="product"
        documentId={selectedDocumentId}
      />
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, CFV2Reducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  cfV2DocConsumption: CFV2Reducers.cfV2DocConsumption,
});

ConsumptionLinesView.propTypes = {
  loading: PropTypes.bool,
  handleProductChangeValue: PropTypes.func,
  handleProductChange: PropTypes.func,
  handleDelete: PropTypes.func,
  updateData: PropTypes.func,
  data: PropTypes.any,
  title: PropTypes.any,
  formSubmitted: PropTypes.bool,
  isLineWiseDiscount: PropTypes.bool,
  addNewRow: PropTypes.func,
  tenantDepartmentId: PropTypes.any,
  MONEY: PropTypes.any,
  pricesToApply: PropTypes.any,
  selectedCurrencyName: PropTypes.any,
};

export default connect(mapStateToProps)(withRouter(ConsumptionLinesView));
